import {
    Component,
    OnInit,
    Input,
    ElementRef,
    HostListener,
    Output,
    EventEmitter,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';

interface ClickedProduct {
    name: string;
    type: 'BANK' | 'P2P' | 'DIGITAL BANK';
}

@Component({
    selector: 'app-product-lists',
    templateUrl: './product-lists.component.html',
    styleUrls: ['./product-lists.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, MatIconModule, TranslateModule],
})
export class ProductListsComponent implements OnInit {
    @Input() dialogClosed: boolean = true;
    @Input() bankProductsList: any;
    @Input() digitalBankProductsList: any;
    @Input() p2pProductsList: any;
    @Input() disableMatchingProductsBtn: boolean = false;
    @Output() openProductsClick: EventEmitter<any> = new EventEmitter<any>();
    @Input() showBanks: boolean = true;
    @Input() showDigitalBanks: boolean = true;
    @Input() showAlternateFinancing: boolean = true;
    showBanksFiltered: boolean = true;
    showDigitalBanksFiltered: boolean = true;
    showAlternateFinancingFiltered: boolean = true;
    @Input() selectedLanguage: string;

    clickedProduct: ClickedProduct | null = null;
    initialBankProductsList = [];
    initialp2pProductsList = [];
    initialDigitalBankProductsList = [];
    totalMatchedProducts: number = 0;
    searchedBankAndp2pList: number = 0;
    searchedProducts = [];
    openProductIndex: number | null = null;
    searchInput: string = '';
    isFilterOpen: boolean = false;
    isFilterOpenMobile: boolean = false;
    isSortbyOpen: boolean = false;
    isSortbyOpenMobile: boolean = false;
    checkboxStates: { [key: string]: boolean } = {};
    selectedSortBy: number = -1;
    filteredProducts: string[] = [];
    productVisibility: { [key: number]: boolean } = {};
    toggledProductIndex: number | null = null;
    // Bank product Details
    displayedBankProducts: any[] = [];
    bankProductsPageSize: number = 6;
    currentBankProductsPage: number = 1;
    showAllBankProducts: boolean = false;
    // Digital Bank product Details
    displayedDigitalBankProducts: any[] = [];
    digitalBankProductsPageSize: number = 6;
    currentDigitalBankProductsPage: number = 1;
    showAllDigitalBankProducts: boolean = false;
    // Add new variables for P2P products pagination
    displayedP2PProducts: any[] = [];
    p2pProductsPageSize: number = 6;
    currentP2PProductsPage: number = 1;
    showAllP2PProducts: boolean = false;
    ga_event: any;
    lastClickedInsideDropdown = false;
    lastClickedInsideDropdownMobile = false;

    constructor(private elRef: ElementRef, private router: Router) {}

    ngOnInit(): void {
        this.initialDigitalBankProductsList = this.nestedCopy(
            this.digitalBankProductsList || []
        );
        // Initialize visibility flags
        this.showBanksFiltered = true;
        this.showDigitalBanksFiltered = true;
        this.showAlternateFinancingFiltered = true;
        this.updateDisplayedBankProducts();
        this.updateDisplayedDigitalBankProducts();
        this.updateDisplayedP2PProducts();
        this.ga_event = [
            {
                calculateFinancing: {
                    id: this.showBanks
                        ? 'calculate_my_financing_msmefinancing_click'
                        : 'calculate_my_financing_dgp_click',
                },
                alternateFinancing: {
                    id: this.showBanks
                        ? '_alternate_financing_card_msmefinancing_click'
                        : '_alternate_financing_card_dgp_click',
                },
                category: this.showBanks
                    ? 'MSME Financing Button'
                    : 'Digital Guarantee Button',
            },
        ];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['bankProductsList']) {
            this.initialBankProductsList = this.nestedCopy(
                this.bankProductsList
            );
            this.resetPagination();
        }
        if (changes['p2pProductsList']) {
            this.initialp2pProductsList = this.nestedCopy(this.p2pProductsList);
            this.resetP2PPagination();
        }
        if (changes['digitalBankProductsList']) {
            this.initialDigitalBankProductsList = this.nestedCopy(
                this.digitalBankProductsList
            );
            this.resetDigitalBankPagination();
        }
    }

    openProducts(bankDetails, type: 'BANK' | 'P2P' | 'DIGITAL BANK') {
        this.clickedProduct = { name: bankDetails.fiPartnerDescBm, type };
        this.openProductsClick.emit({ bankDetails });
    }

    toggleFilter(isOpen: boolean) {
        if (this.isFilterOpen === isOpen) {
            this.closeFilterBy();
            return;
        }
        this.isFilterOpen = isOpen;
        this.isSortbyOpen = false;
    }

    toggleSortBy(isOpen: boolean) {
        if (this.isSortbyOpen === isOpen) {
            this.closeSortBy();
            return;
        }
        this.isSortbyOpen = isOpen;
        this.isFilterOpen = false;
    }

    closeSortBy() {
        this.isSortbyOpen = false;
    }

    closeFilterBy() {
        this.isFilterOpen = false;
    }

    toggleFilterMobile() {
        this.lastClickedInsideDropdownMobile = true;
        this.isFilterOpenMobile = !this.isFilterOpenMobile;
        this.isSortbyOpenMobile = false;
    }

    toggleSortMobile() {
        this.lastClickedInsideDropdownMobile = true;
        this.isSortbyOpenMobile = !this.isSortbyOpenMobile;
        this.isFilterOpenMobile = false;
    }

    @HostListener('document:click', ['$event'])
    handleClickOutside(event: MouseEvent): void {
        if (this.lastClickedInsideDropdown) {
            // Reset the flag and ignore this click
            this.lastClickedInsideDropdown = false;
            return;
        }

        if (this.lastClickedInsideDropdownMobile) {
            // Reset the flag and ignore this click
            this.lastClickedInsideDropdownMobile = false;
            return;
        }

        const target = event.target as HTMLElement;

        const clickedInsideFilterMobile = target.closest(
            '.filter-container-mobile'
        );
        const clickedInsideSortMobile = target.closest(
            '.sort-container-mobile'
        );
        const clickedInsideFilter = target.closest('.filter-container');
        const clickedInsideSort = target.closest('.sort-container');

        if (!clickedInsideFilter) {
            this.isFilterOpen = false;
        }
        if (!clickedInsideFilterMobile) {
            this.isFilterOpenMobile = false;
        }

        if (!clickedInsideSort) {
            this.isSortbyOpen = false;
        }

        if (!clickedInsideSortMobile) {
            this.isSortbyOpenMobile = false;
        }
    }

    displayProducts(index: number) {
        this.openProductIndex = this.openProductIndex === index ? null : index;
    }

    toggleAllProducts(index: number) {
        this.productVisibility[index] = !this.productVisibility[index];
    }

    toggleProducts(itemId: number) {
        this.toggledProductIndex = itemId;
    }

    closeProducts() {
        this.toggledProductIndex = null;
        this.clickedProduct = null;
    }

    addIfNotExists(value: any, array: any[]): void {
        // Check if the value is not already present in the array
        if (array.indexOf(value) === -1) {
            // If not present, add the value to the array
            array.push(value);
        }
    }

    removeProviders(prodCat) {
        let removeIndex = this.filteredProducts.findIndex(
            (item) => item === prodCat
        );
        if (removeIndex !== -1) {
            this.filteredProducts.splice(removeIndex, 1);
        }
    }

    onFilterProduct(event, prodCategory) {
        this.searchInput = '';
        this.searchedProducts = [];

        let prodCat = prodCategory;
        this.checkboxStates[prodCat] = event.target.checked;

        if (prodCat === 'Provider') {
            this.checkboxStates['BANK'] = event.target.checked;
            this.checkboxStates['P2P'] = event.target.checked;
            this.checkboxStates['CGC'] = event.target.checked;
            this.checkboxStates['DIGITAL BANK'] = event.target.checked;
            if (event.target.checked) {
                this.addIfNotExists('BANK', this.filteredProducts);
                this.addIfNotExists('P2P', this.filteredProducts);
                this.addIfNotExists('CGC', this.filteredProducts);
                this.addIfNotExists('DIGITAL BANK', this.filteredProducts);
            } else {
                this.removeProviders('BANK');
                this.removeProviders('P2P');
                this.removeProviders('CGC');
                this.removeProviders('DIGITAL BANK');
            }

            this.bankProductsList = this.nestedCopy(
                this.initialBankProductsList
            );
            this.p2pProductsList = this.nestedCopy(this.initialp2pProductsList);
            this.digitalBankProductsList = this.nestedCopy(
                this.initialDigitalBankProductsList
            );

            // Apply all filters at once
            this.filteredProducts.forEach((filter) => {
                this.filterProductFn(filter);
            });
        } else if (prodCat === 'Type') {
            this.checkboxStates['isIslamic'] = event.target.checked;
            this.checkboxStates['isConventional'] = event.target.checked;
            if (event.target.checked) {
                this.addIfNotExists('isIslamic', this.filteredProducts);
                this.addIfNotExists('isConventional', this.filteredProducts);
            } else {
                this.removeProviders('isIslamic');
                this.removeProviders('isConventional');
            }
            this.bankProductsList = this.nestedCopy(
                this.initialBankProductsList
            );
            this.p2pProductsList = this.nestedCopy(this.initialp2pProductsList);
            this.digitalBankProductsList = this.nestedCopy(
                this.initialDigitalBankProductsList
            );

            // Apply all filters at once
            this.filteredProducts.forEach((filter) => {
                this.filterProductFn(filter);
            });
        } else {
            if (event.target.checked) {
                this.filteredProducts.push(prodCat);
            } else {
                if (
                    prodCat === 'BANK' ||
                    prodCat === 'P2P' ||
                    prodCat === 'CGC' ||
                    prodCat === 'DIGITAL BANK'
                ) {
                    this.checkboxStates['Provider'] = false;
                }
                if (prodCat === 'isIslamic' || prodCat === 'isConventional') {
                    this.checkboxStates['Type'] = false;
                }
                let removeIndex = this.filteredProducts.findIndex(
                    (item) => item === prodCat
                );
                if (removeIndex !== -1) {
                    this.filteredProducts.splice(removeIndex, 1);
                }
            }

            // Reset to the original list before applying filters
            this.bankProductsList = this.nestedCopy(
                this.initialBankProductsList
            );
            this.p2pProductsList = this.nestedCopy(this.initialp2pProductsList);
            this.digitalBankProductsList = this.nestedCopy(
                this.initialDigitalBankProductsList
            );

            // Apply all filters at once
            this.filteredProducts.forEach((filter) => {
                this.filterProductFn(filter);
            });

            // Update visibility flags after filtering
            let providerFilters = this.filteredProducts.filter(
                (item) => item !== 'isIslamic' && item !== 'isConventional'
            );
            if (providerFilters.length > 0) {
                this.showBanksFiltered =
                    providerFilters.includes('BANK') ||
                    providerFilters.includes('CGC');
                this.showDigitalBanksFiltered =
                    providerFilters.includes('DIGITAL BANK');
                this.showAlternateFinancingFiltered =
                    providerFilters.includes('P2P');
            } else {
                this.showBanksFiltered = true;
                this.showDigitalBanksFiltered = true;
                this.showAlternateFinancingFiltered = true;
            }

            if (
                this.filteredProducts.includes('BANK') &&
                this.filteredProducts.includes('P2P') &&
                this.filteredProducts.includes('CGC') &&
                this.filteredProducts.includes('DIGITAL BANK')
            ) {
                this.checkboxStates['Provider'] = true;
            }
            if (
                this.filteredProducts.includes('isIslamic') &&
                this.filteredProducts.includes('isConventional')
            ) {
                this.checkboxStates['Type'] = true;
            }
        }
        this.resetPagination();
        this.resetDigitalBankPagination();
        this.resetP2PPagination();
    }

    hasAnyTrueFlag(obj: Record<string, boolean>): boolean {
        if (!obj || Object.keys(obj).length === 0) {
            return false;
        }

        return Object.values(obj).some((value) => value === true);
    }

    filterProductFn(prodCat) {
        // Combine filters for Providers with OR relationship
        if (prodCat !== 'isIslamic' && prodCat !== 'isConventional') {
            let providerFilters = this.filteredProducts.filter(
                (item) => item !== 'isIslamic' && item !== 'isConventional'
            );

            // Update visibility flags based on what's being filtered
            if (providerFilters.length > 0) {
                this.showBanksFiltered =
                    providerFilters.includes('BANK') ||
                    providerFilters.includes('CGC');
                this.showDigitalBanksFiltered =
                    providerFilters.includes('DIGITAL BANK');
                this.showAlternateFinancingFiltered =
                    providerFilters.includes('P2P');
            } else {
                // If no provider filters, show all sections
                this.showBanksFiltered = true;
                this.showDigitalBanksFiltered = true;
                this.showAlternateFinancingFiltered = true;
            }

            this.bankProductsList = this.bankProductsList
                .map((bank) => {
                    const filteredProducts = bank.products.filter((product) => {
                        return providerFilters.includes(
                            product.productCategory
                        );
                    });
                    return { ...bank, products: filteredProducts };
                })
                .filter((bank) => bank.products.length > 0);
            this.p2pProductsList = this.p2pProductsList
                .map((bank) => {
                    const filteredProducts = bank.products.filter((product) => {
                        return providerFilters.includes(
                            product.productCategory
                        );
                    });
                    return { ...bank, products: filteredProducts };
                })
                .filter((bank) => bank.products.length > 0);
            this.digitalBankProductsList = this.digitalBankProductsList
                .map((bank) => {
                    const filteredProducts = bank.products.filter((product) => {
                        return providerFilters.includes(
                            product.productCategory
                        );
                    });
                    return { ...bank, products: filteredProducts };
                })
                .filter((bank) => bank.products.length > 0);
        } else {
            // If no provider filters are active, show all sections
            if (
                !this.filteredProducts.some((filter) =>
                    ['BANK', 'P2P', 'CGC', 'DIGITAL BANK'].includes(filter)
                )
            ) {
                this.showBanksFiltered = true;
                this.showDigitalBanksFiltered = true;
                this.showAlternateFinancingFiltered = true;
            }
        }

        // Combine filters for Types with OR relationship
        if (prodCat === 'isIslamic' || prodCat === 'isConventional') {
            let typeFilter =
                this.filteredProducts.includes('isIslamic') &&
                this.filteredProducts.includes('isConventional');
            this.bankProductsList = this.bankProductsList
                .map((bank) => {
                    const filteredProducts = bank.products.filter((product) => {
                        return (
                            typeFilter ||
                            product.prodShariah ===
                                (prodCat === 'isIslamic' ? 'Y' : 'N')
                        );
                    });
                    return { ...bank, products: filteredProducts };
                })
                .filter((bank) => bank.products.length > 0);
            this.p2pProductsList = this.p2pProductsList
                .map((bank) => {
                    const filteredProducts = bank.products.filter((product) => {
                        return (
                            typeFilter ||
                            product.prodShariah ===
                                (prodCat === 'isIslamic' ? 'Y' : 'N')
                        );
                    });
                    return { ...bank, products: filteredProducts };
                })
                .filter((bank) => bank.products.length > 0);
            this.digitalBankProductsList = this.digitalBankProductsList
                .map((bank) => {
                    const filteredProducts = bank.products.filter((product) => {
                        return (
                            typeFilter ||
                            product.prodShariah ===
                                (prodCat === 'isIslamic' ? 'Y' : 'N')
                        );
                    });
                    return { ...bank, products: filteredProducts };
                })
                .filter((bank) => bank.products.length > 0);
        }

        this.searchedBankAndp2pList =
            this.bankProductsList.length +
            this.p2pProductsList.length +
            this.digitalBankProductsList.length;
        this.totalMatchedProducts = this.searchedBankAndp2pList;
        this.sortProductFn();
    }

    updateFilteredProductsList() {
        // Reset to the filtered list before applying sorting
        this.bankProductsList = this.nestedCopy(this.initialBankProductsList);
        this.p2pProductsList = this.nestedCopy(this.initialp2pProductsList);
        this.digitalBankProductsList = this.nestedCopy(
            this.initialDigitalBankProductsList
        );

        // Reset visibility flags
        let providerFilters = this.filteredProducts.filter(
            (item) => item !== 'isIslamic' && item !== 'isConventional'
        );
        if (providerFilters.length > 0) {
            this.showBanksFiltered =
                providerFilters.includes('BANK') ||
                providerFilters.includes('CGC');
            this.showDigitalBanksFiltered =
                providerFilters.includes('DIGITAL BANK');
            this.showAlternateFinancingFiltered =
                providerFilters.includes('P2P');
        } else {
            this.showBanksFiltered = true;
            this.showDigitalBanksFiltered = true;
            this.showAlternateFinancingFiltered = true;
        }

        // Apply all filters first
        this.filteredProducts.forEach((filter) => {
            this.filterProductFn(filter);
        });

        this.sortProductFn();
    }

    onSortProduct(index: number) {
        this.searchInput = '';
        if (this.selectedSortBy === index) {
            this.selectedSortBy = -1;
        } else {
            this.selectedSortBy = index;
        }
        this.updateFilteredProductsList();
        this.resetPagination();
        this.resetDigitalBankPagination();
        this.resetP2PPagination();
    }

    sortProductFn() {
        if (this.selectedSortBy === -1) {
            return;
        }

        if (this.searchedProducts.length) {
            this.searchedProducts = [];
            this.bankProductsList = this.nestedCopy(
                this.initialBankProductsList
            );
            this.p2pProductsList = this.nestedCopy(this.initialp2pProductsList);
            this.digitalBankProductsList = this.nestedCopy(
                this.initialDigitalBankProductsList
            );
        }

        const sortOrder =
            this.selectedSortBy === 0 || this.selectedSortBy === 2
                ? 'desc'
                : 'asc';
        const attribute =
            this.selectedSortBy === 0 || this.selectedSortBy === 1
                ? 'prodInterestRate'
                : 'prodTat';

        const compareProducts = (a, b, attribute, sortOrder) => {
            const valueA = parseFloat(a[attribute]);
            const valueB = parseFloat(b[attribute]);

            let comparisonResult =
                sortOrder === 'asc' ? valueA - valueB : valueB - valueA;

            if (comparisonResult === 0) {
                const nameA = a.productName.toLowerCase();
                const nameB = b.productName.toLowerCase();
                comparisonResult = nameA.localeCompare(nameB);
            }

            return comparisonResult;
        };

        this.bankProductsList = this.bankProductsList.map((bank) => {
            bank.products.sort((a, b) =>
                compareProducts(a, b, attribute, sortOrder)
            );
            return bank;
        });
        this.p2pProductsList = this.p2pProductsList.map((bank) => {
            bank.products.sort((a, b) =>
                compareProducts(a, b, attribute, sortOrder)
            );
            return bank;
        });
        this.digitalBankProductsList = this.digitalBankProductsList.map(
            (bank) => {
                bank.products.sort((a, b) =>
                    compareProducts(a, b, attribute, sortOrder)
                );
                return bank;
            }
        );

        this.bankProductsList.sort((a, b) =>
            compareProducts(a.products[0], b.products[0], attribute, sortOrder)
        );
        this.p2pProductsList.sort((a, b) =>
            compareProducts(a.products[0], b.products[0], attribute, sortOrder)
        );
        this.digitalBankProductsList.sort((a, b) =>
            compareProducts(a.products[0], b.products[0], attribute, sortOrder)
        );
    }

    onSearchProduct(searchText: string) {
        if (!searchText) {
            this.bankProductsList = this.initialBankProductsList;
            this.p2pProductsList = this.initialp2pProductsList;
            this.digitalBankProductsList = this.initialDigitalBankProductsList;
            this.totalMatchedProducts = 0;
            // Reset visibility flags when clearing search
            this.showBanksFiltered = true;
            this.showDigitalBanksFiltered = true;
            this.showAlternateFinancingFiltered = true;
            return;
        }

        let matchedBankProducts = 0;
        this.bankProductsList = this.initialBankProductsList
            .map((bank) => {
                const filteredProducts = bank.products.filter((product) =>
                    product.productName
                        .toLowerCase()
                        .includes(searchText.toLowerCase())
                );
                const bankMatches = bank.fiPartnerDescEng
                    .toLowerCase()
                    .includes(searchText.toLowerCase());
                if (bankMatches || filteredProducts.length > 0) {
                    matchedBankProducts += filteredProducts.length;
                    return {
                        ...bank,
                        products:
                            filteredProducts.length > 0
                                ? filteredProducts
                                : bank.products,
                    };
                }
                return null;
            })
            .filter((bank) => bank !== null);

        let matchedp2pProducts = 0;
        this.p2pProductsList = this.initialp2pProductsList
            .map((bank) => {
                const filteredProducts = bank.products.filter((product) =>
                    product.productName
                        .toLowerCase()
                        .includes(searchText.toLowerCase())
                );
                const bankMatches = bank.fiPartnerDescEng
                    .toLowerCase()
                    .includes(searchText.toLowerCase());
                if (bankMatches || filteredProducts.length > 0) {
                    matchedp2pProducts += filteredProducts.length;
                    return {
                        ...bank,
                        products:
                            filteredProducts.length > 0
                                ? filteredProducts
                                : bank.products,
                    };
                }
                return null;
            })
            .filter((bank) => bank !== null);

        let matchedDigitalBankProducts = 0;
        this.digitalBankProductsList = this.initialDigitalBankProductsList
            .map((bank) => {
                const filteredProducts = bank.products.filter((product) =>
                    product.productName
                        .toLowerCase()
                        .includes(searchText.toLowerCase())
                );
                const bankMatches = bank.fiPartnerDescEng
                    .toLowerCase()
                    .includes(searchText.toLowerCase());
                if (bankMatches || filteredProducts.length > 0) {
                    matchedDigitalBankProducts += filteredProducts.length;
                    return {
                        ...bank,
                        products:
                            filteredProducts.length > 0
                                ? filteredProducts
                                : bank.products,
                    };
                }
                return null;
            })
            .filter((bank) => bank !== null);

        this.totalMatchedProducts =
            matchedBankProducts +
            matchedp2pProducts +
            matchedDigitalBankProducts;
        this.searchedBankAndp2pList =
            this.bankProductsList.length +
            this.p2pProductsList.length +
            this.digitalBankProductsList.length;

        // clear filters and sort by in case of search
        this.checkboxStates['Provider'] = false;
        this.checkboxStates['BANK'] = false;
        this.checkboxStates['P2P'] = false;
        this.checkboxStates['CGC'] = false;
        this.checkboxStates['DIGITAL BANK'] = false;
        this.checkboxStates['Type'] = false;
        this.checkboxStates['isIslamic'] = false;
        this.checkboxStates['isConventional'] = false;
        this.filteredProducts = [];
        this.selectedSortBy = -1;
        // Reset visibility flags when searching
        this.showBanksFiltered = true;
        this.showDigitalBanksFiltered = true;
        this.showAlternateFinancingFiltered = true;
        this.resetPagination();
        this.resetDigitalBankPagination();
        this.resetP2PPagination();
    }

    nestedCopy(array) {
        return Array.from(array);
    }

    getUnitFromValidation(validationString: string): string {
        if (!validationString || validationString === 'null') {
            return 'YEARS';
        }
        try {
            const validation = JSON.parse(validationString);
            return validation?.tenure?.unit?.toUpperCase() || 'YEARS';
        } catch (e) {
            return 'YEARS';
        }
    }

    getImgUrl(img: string): string {
        if (img) {
            return img;
        }
        return this.selectedLanguage === 'bm'
            ? '/assets/images/image_not_available_bm.webp'
            : '/assets/images/image_not_available_en.webp';
    }

    closeMegaMenuCondition() {
        // Stub implementation
    }

    calculateFinancing() {
        this.router.navigate(['/financing-calculator']);
    }

    resetPagination() {
        this.currentBankProductsPage = 1;
        this.showAllBankProducts = false;
        this.updateDisplayedBankProducts();
    }

    resetDigitalBankPagination() {
        this.currentDigitalBankProductsPage = 1;
        this.showAllDigitalBankProducts = false;
        this.updateDisplayedDigitalBankProducts();
    }

    resetP2PPagination() {
        this.currentP2PProductsPage = 1;
        this.showAllP2PProducts = false;
        this.updateDisplayedP2PProducts();
    }

    updateDisplayedBankProducts() {
        if (!this.bankProductsList) return;

        const startIndex = 0;
        const endIndex =
            this.currentBankProductsPage * this.bankProductsPageSize;
        this.displayedBankProducts = this.bankProductsList.slice(
            startIndex,
            endIndex
        );
        this.showAllBankProducts =
            this.displayedBankProducts.length >= this.bankProductsList.length;
    }

    updateDisplayedDigitalBankProducts() {
        if (!this.digitalBankProductsList) return;

        const startIndex = 0;
        const endIndex =
            this.currentDigitalBankProductsPage *
            this.digitalBankProductsPageSize;
        this.displayedDigitalBankProducts = this.digitalBankProductsList.slice(
            startIndex,
            endIndex
        );
        this.showAllDigitalBankProducts =
            this.displayedDigitalBankProducts.length >=
            this.digitalBankProductsList.length;
    }

    updateDisplayedP2PProducts() {
        if (!this.p2pProductsList) return;

        const startIndex = 0;
        const endIndex = this.currentP2PProductsPage * this.p2pProductsPageSize;
        this.displayedP2PProducts = this.p2pProductsList.slice(
            startIndex,
            endIndex
        );
        this.showAllP2PProducts =
            this.displayedP2PProducts.length >= this.p2pProductsList.length;
    }

    toggleShowMoreBankProducts() {
        if (this.showAllBankProducts) return;

        this.currentBankProductsPage++;
        this.updateDisplayedBankProducts();
    }

    toggleShowMoreDigitalBankProducts() {
        if (this.showAllDigitalBankProducts) return;

        this.currentDigitalBankProductsPage++;
        this.updateDisplayedDigitalBankProducts();
    }

    toggleShowMoreP2PProducts() {
        if (this.showAllP2PProducts) return;

        this.currentP2PProductsPage++;
        this.updateDisplayedP2PProducts();
    }
}
